(function () {
  'use strict';

  class HeroWavyMarquee {
    constructor(root) {
      this.root = root;
      this.svg = root.querySelector('[data-wavy-marquee-svg]');
      this.ribbon = root.querySelector('[data-wavy-marquee-ribbon]');
      this.textPathEl = root.querySelector('[data-wavy-marquee-text]');
      this.textPathShape = root.querySelector('[data-wavy-marquee-text-path]');
      this.rafId = null;
      this.resizeObserver = null;
      this.offset = 0;
      this.lastTime = null;
      this.centerPath = '';
      this.init();
    }

    destroy() {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }

    getMetrics() {
      const width = Math.max(Math.round(this.root.clientWidth), 1);
      const height = Math.max(
        parseFloat(getComputedStyle(this.root).getPropertyValue('--hero-wmb-height')) ||
          this.root.clientHeight ||
          56,
        1
      );
      let wavelength = parseFloat(this.root.dataset.waveLength) || 260;
      wavelength = Math.min(420, Math.max(160, wavelength));

      const amplitude = Math.min(10, Math.max(4, height * 0.14));
      const bandHeight = Math.max(18, height - amplitude * 2 - 4);

      return { width, height, wavelength, amplitude, bandHeight };
    }

    waveY(x, midY, amplitude, wavelength) {
      return midY + amplitude * Math.sin((2 * Math.PI * x) / wavelength);
    }

    buildPaths(width, height, wavelength, amplitude, bandHeight) {
      const midY = height / 2;
      const halfBand = bandHeight / 2;
      const step = Math.max(4, Math.round(wavelength / 24));
      const endX = width + wavelength;

      const topPoints = [];
      const bottomPoints = [];
      const centerPoints = [];

      for (let x = -wavelength; x <= endX; x += step) {
        const wave = this.waveY(x, midY, amplitude, wavelength);
        topPoints.push([x, wave - halfBand]);
        bottomPoints.push([x, wave + halfBand]);
        centerPoints.push([x, wave]);
      }

      const lastTop = topPoints[topPoints.length - 1];
      const lastBottom = bottomPoints[bottomPoints.length - 1];
      const firstTop = topPoints[0];
      const firstBottom = bottomPoints[0];

      const ribbonPath = [
        `M ${firstTop[0]} ${firstTop[1]}`,
        ...topPoints.slice(1).map((point) => `L ${point[0]} ${point[1]}`),
        `L ${lastBottom[0]} ${lastBottom[1]}`,
        ...bottomPoints
          .slice(0, -1)
          .reverse()
          .map((point) => `L ${point[0]} ${point[1]}`),
        'Z',
      ].join(' ');

      const centerPath = [
        `M ${centerPoints[0][0]} ${centerPoints[0][1]}`,
        ...centerPoints.slice(1).map((point) => `L ${point[0]} ${point[1]}`),
      ].join(' ');

      return { ribbonPath, centerPath };
    }

    renderGeometry() {
      if (!this.svg || !this.ribbon || !this.textPathShape) return false;

      const { width, height, wavelength, amplitude, bandHeight } = this.getMetrics();
      const { ribbonPath, centerPath } = this.buildPaths(
        width,
        height,
        wavelength,
        amplitude,
        bandHeight
      );

      this.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      this.svg.setAttribute('width', String(width));
      this.svg.setAttribute('height', String(height));
      this.ribbon.setAttribute('d', ribbonPath);
      this.textPathShape.setAttribute('d', centerPath);
      this.centerPath = centerPath;

      return true;
    }

    startScroll() {
      if (!this.textPathEl || !this.centerPath) return;

      const message = (this.root.dataset.text || '').trim();
      if (!message) return;

      let speed = parseFloat(this.root.dataset.speed) || 40;
      speed = Math.min(120, Math.max(10, speed));

      const direction = this.root.dataset.direction === 'right' ? 1 : -1;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const separator = '   ';
      const chunk = `${message}${separator}`;
      let content = chunk;

      this.textPathEl.textContent = content;

      const pathLength = this.textPathShape.getTotalLength();
      let safety = 0;

      while (this.textPathEl.getComputedTextLength() < pathLength * 2 && safety < 40) {
        content += chunk;
        this.textPathEl.textContent = content;
        safety += 1;
      }

      const loopLength = this.textPathEl.getComputedTextLength() / 2;
      this.offset = 0;
      this.lastTime = null;
      this.textPathEl.setAttribute('startOffset', '0');

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      if (reducedMotion || loopLength <= 0 || pathLength <= 0) return;

      const tick = (time) => {
        if (this.lastTime !== null) {
          const delta = (time - this.lastTime) / 1000;
          this.offset += direction * speed * delta;

          if (direction < 0 && this.offset <= -loopLength) {
            this.offset += loopLength;
          } else if (direction > 0 && this.offset >= loopLength) {
            this.offset -= loopLength;
          }

          this.textPathEl.setAttribute('startOffset', `${this.offset}px`);
        }

        this.lastTime = time;
        this.rafId = window.requestAnimationFrame(tick);
      };

      this.rafId = window.requestAnimationFrame(tick);
    }

    refresh() {
      if (!this.renderGeometry()) return;
      this.startScroll();
    }

    init() {
      if (!this.svg || !this.ribbon || !this.textPathEl || !this.textPathShape) return;

      const run = () => {
        if (this.root.clientWidth <= 0) {
          window.requestAnimationFrame(run);
          return;
        }
        this.refresh();
      };

      run();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.root.clientWidth > 0) this.refresh();
        });
        this.resizeObserver.observe(this.root);
      } else {
        window.addEventListener('resize', () => {
          if (this.root.clientWidth > 0) this.refresh();
        });
      }
    }
  }

  class HeroSlider {
    constructor(el) {
      if (el.dataset.heroSliderInit === 'true') return;
      el.dataset.heroSliderInit = 'true';

      this.el = el;
      this.slides = [...el.querySelectorAll('[data-hero-slide]')];
      this.dots = [...el.querySelectorAll('[data-hero-dot]')];
      this.mobileButtons = [...el.querySelectorAll('[data-hero-mobile-buttons]')];
      this.current = 0;
      this.timer = null;
      this.autoplay = el.dataset.autoplay === 'true';
      this.speed = parseInt(el.dataset.autoplaySpeed, 10) || 5000;
      this.touchStartX = 0;
      this.hasTextAnimation = el.className.includes('hero-slider--text-anim-');

      if (this.slides.length > 1) {
        el.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.goTo(this.current - 1));
        el.querySelector('[data-hero-next]')?.addEventListener('click', () => this.goTo(this.current + 1));

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.heroDot, 10)));
        });

        el.addEventListener(
          'touchstart',
          (e) => {
            this.touchStartX = e.changedTouches[0].screenX;
          },
          { passive: true }
        );

        el.addEventListener(
          'touchend',
          (e) => {
            const diff = e.changedTouches[0].screenX - this.touchStartX;
            if (Math.abs(diff) > 50) {
              this.goTo(diff > 0 ? this.current - 1 : this.current + 1);
            }
          },
          { passive: true }
        );

        el.addEventListener('mouseenter', () => this.pause());
        el.addEventListener('mouseleave', () => this.play());

        this.play();
      }

      this.slides.forEach((slide) => {
        slide.classList.remove('is-text-ready', 'is-media-ready');
      });

      this.activateSlide(this.current, true);
    }

    buildEmbedSrc(type, id) {
      if (type === 'youtube') {
        return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&modestbranding=1&loop=1&playlist=${id}`;
      }
      if (type === 'vimeo') {
        return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&background=1&loop=1`;
      }
      return '';
    }

    manageSlideMedia(index) {
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        const videoWrap = slide.querySelector('[data-hero-video-wrap]');
        const video = slide.querySelector('.hero-slider__video');
        const iframe = slide.querySelector('[data-hero-iframe]');

        if (video) {
          const shouldAutoplay = video.dataset.heroVideoAutoplay !== 'false';
          if (isActive && shouldAutoplay) {
            video.play().catch(() => {});
          } else {
            video.pause();
            if (!video.loop) video.currentTime = 0;
          }
          return;
        }

        if (!videoWrap || videoWrap.dataset.heroVideoType !== 'embed' || !iframe) return;

        const type = videoWrap.dataset.embedType;
        const id = videoWrap.dataset.embedId;
        if (!type || !id) return;

        iframe.src = isActive ? this.buildEmbedSrc(type, id) : '';
      });
    }

    revealSlideContent(slide, immediate) {
      if (!slide) return;

      const reveal = () => {
        slide.classList.add('is-media-ready');
        slide.classList.add('is-text-ready');
      };

      slide.classList.remove('is-text-ready', 'is-media-ready');

      if (immediate) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(reveal);
        });
        return;
      }

      window.setTimeout(reveal, 80);
    }

    activateSlide(index, immediate) {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        if (i !== index) {
          slide.classList.remove('is-text-ready', 'is-media-ready');
        }
      });

      if (this.dots.length) {
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          if (i === index) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      if (this.mobileButtons.length) {
        this.mobileButtons.forEach((panel, i) => {
          panel.classList.toggle('is-active', i === index);
        });
      }

      this.manageSlideMedia(index);
      this.revealSlideContent(this.slides[index], immediate);
    }

    goTo(index) {
      const next = (index + this.slides.length) % this.slides.length;
      if (next === this.current) return;

      this.current = next;
      this.activateSlide(this.current, false);
      this.pause();
      this.play();
    }

    play() {
      if (!this.autoplay || this.slides.length <= 1) return;
      this.timer = setInterval(() => this.goTo(this.current + 1), this.speed);
    }

    pause() {
      clearInterval(this.timer);
    }
  }

  const initHeroSliders = (root = document) => {
    root.querySelectorAll('[data-hero-slider]').forEach((el) => new HeroSlider(el));
  };

  const initWavyMarquees = (root = document) => {
    root.querySelectorAll('[data-wavy-marquee]').forEach((el) => {
      if (el._heroWavyMarquee) {
        el._heroWavyMarquee.destroy();
      }
      el._heroWavyMarquee = new HeroWavyMarquee(el);
    });
  };

  const initHero = (root = document) => {
    initHeroSliders(root);
    initWavyMarquees(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHero());
  } else {
    initHero();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initHero(event.target);
  });
})();
